// ── Adapter Supabase do IUsersRepository ──────────────────────────
// Lista usuários do workspace por papel via times + time_members + profiles.

import { getSupabase } from '@infra/supabase/client';
import type { IUsersRepository, UserLite, ProfileLite } from '@modules/config';

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase não inicializado.');
  return s;
};

export class SupabaseUsersRepository implements IUsersRepository {
  listUsersByRole = async (workspaceId: string, role: 'sdr' | 'closer'): Promise<UserLite[]> => {
    const supabase = client();

    const { data: times } = await supabase
      .from('times')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('ativo', true);

    const timeIds = (times ?? []).map((t) => t.id);
    if (timeIds.length === 0) return [];

    const { data, error } = await supabase
      .from('time_members')
      .select(
        'user_id, role, profile:profiles!time_members_user_id_fkey ( id, nome, initials, color, avatar_url, ativo )',
      )
      .in('time_id', timeIds)
      .eq('role', role)
      .eq('ativo', true);
    if (error) throw error;

    const seen = new Set<string>();
    const out: UserLite[] = [];
    // O join aninhado não é fortemente tipável pelo helper; tratamos como any.
    for (const row of (data ?? []) as any[]) {
      const p = row.profile;
      if (!p || p.ativo === false || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push({
        id: p.id,
        nome: p.nome,
        initials: p.initials,
        color: p.color,
        avatar_url: p.avatar_url,
        ativo: p.ativo ?? null,
      });
    }
    return out;
  };

  getProfilesByIds = async (ids: string[]): Promise<ProfileLite[]> => {
    if (!ids?.length) return [];
    const { data } = await client()
      .from('profiles')
      .select('id, nome, initials, color, avatar_url')
      .in('id', ids);
    return (data || []) as unknown as ProfileLite[];
  };

  updateProfileName = async (profileId: string, nome: string, initials: string): Promise<void> => {
    const { error } = await client()
      .from('profiles')
      .update({ nome, initials })
      .eq('id', profileId);
    if (error) throw error;
  };
}
